'use client';

import type { SubmissionResult } from '@morfi/backend-sdk-v2/client';
import { type AutoTradePending, useChartSettingsStore } from '@/stores/chart-settings';

export const QUICK_TRADE_RESULT_HOLD_MS = 450;

/**
 * Publish one chip's real submission lifecycle without coupling it to the
 * mutation hook's aggregate pending state. Object identity prevents an older
 * rapid trade from settling or clearing a newer chip animation for the market.
 */
export async function runWithQuickTradeProgress(
  market: string,
  chip: AutoTradePending | undefined,
  run: (onConfirmed: () => void) => Promise<SubmissionResult>,
): Promise<SubmissionResult> {
  if (chip === undefined) return run(() => undefined);

  const pending: AutoTradePending = { ...chip, phase: 'pending' };
  useChartSettingsStore.getState().setAutoTradePending(market, pending);
  const confirm = (): void => settleQuickTrade(market, pending, 'success');

  try {
    const result = await run(confirm);
    settleQuickTrade(market, pending, result.ok ? 'success' : 'error');
    return result;
  } catch (error) {
    settleQuickTrade(market, pending, 'error');
    throw error;
  }
}

function settleQuickTrade(
  market: string,
  pending: AutoTradePending,
  phase: 'success' | 'error',
): void {
  const store = useChartSettingsStore.getState();
  if (store.autoTradePending[market] !== pending) return;

  const settled: AutoTradePending = { ...pending, phase };
  store.setAutoTradePending(market, settled);
  window.setTimeout(() => {
    const current = useChartSettingsStore.getState();
    if (current.autoTradePending[market] === settled) {
      current.setAutoTradePending(market, null);
    }
  }, QUICK_TRADE_RESULT_HOLD_MS);
}
